import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { isPackageShareable } from '../utils/packageShareable';
import { useAuth } from '../contexts/AuthContext';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import SEOHead from '../components/SEOHead';
import StripeCheckout from '../components/StripeCheckout';
import { getPackageSchema, getBreadcrumbSchema, generatePackageMetaDescription, generatePackageKeywords } from '../utils/seoSchemas';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import PublicPackagePage from '../components/PublicPackagePage';
import PrivateJourneyPackageView from '../components/PrivateJourneyPackageView';
import { isPackageExpired } from '../utils/partnerListUtils';
import { useSyncClientBranding } from '../contexts/ClientBrandingContext';
import { getPartnerCatalogToken, getPackagesListingPath } from '../utils/partnerCatalogStorage';
import BookForClientDialog from '../components/BookForClientDialog';
import { SellerDisclosureBox, sellerFromPackage } from '../components/SellerDisclosures';
import DocumentPageOverlay from '../components/DocumentPageOverlay';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import { addOnSelectionsPayload } from '../utils/packageItems';
import { isPrivateLuxuryPackage } from '../utils/privateJourneyPricing';

const PublicPackageDetails = ({
  previewSlug,
  isPreviewMode = false,
  catalogToken: catalogTokenProp,
  adminPreviewPartnerId,
}) => {
  const { t } = useTranslation();
  const { slug: urlSlug } = useParams();
  const slug = previewSlug || urlSlug; // Use prop if in preview mode, otherwise URL param
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryPreview = searchParams.get('preview') === '1';
  const clientToken = searchParams.get('client');
  const catalogToken = catalogTokenProp || getPartnerCatalogToken();
  const adminPreview = Boolean(adminPreviewPartnerId);
  const partnerPreviewMode = Boolean(isPreviewMode || queryPreview || adminPreview);
  const embedded = Boolean(previewSlug);
  const canUsePartnerPreview = partnerPreviewMode && !adminPreview && !!localStorage.getItem('partnerToken');
  
  const [pkg, setPkg] = useState(null);
  useSyncClientBranding(pkg?.branding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [advisorAskOpen, setAdvisorAskOpen] = useState(false);
  const [inquiryTravelers, setInquiryTravelers] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [stripeCheckoutOpen, setStripeCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isPartnerDraftPreview, setIsPartnerDraftPreview] = useState(false);
  const [clientLinkError, setClientLinkError] = useState('');
  const [clientLinkNotice, setClientLinkNotice] = useState('');
  const [copyLinkBusy, setCopyLinkBusy] = useState(false);
  const [bookForClientOpen, setBookForClientOpen] = useState(false);
  // Seller of Travel §17550.13: disclosures must be shown before payment.
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [buyerCountry, setBuyerCountry] = useState('US');
  const [buyerState, setBuyerState] = useState('');
  const [disclosureConfirmed, setDisclosureConfirmed] = useState(false);
  const [selectedAddOnQuantities, setSelectedAddOnQuantities] = useState({});
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const loadPackage = useCallback(async () => {
    if (!slug) {
        setError('notFound');
        setLoading(false);
        return;
    }

    try {
      setLoading(true);
      setError(null);

      let response;
      let usedPartnerPreview = false;
      const catalogQuery = catalogToken
        ? `catalog=${encodeURIComponent(catalogToken)}`
        : '';
      const clientQuery = clientToken
        ? `client=${encodeURIComponent(clientToken)}`
        : '';
      const queryParts = [clientQuery, catalogQuery].filter(Boolean);
      const publicPackagePath = queryParts.length
        ? `/travel-packages/${slug}?${queryParts.join('&')}`
        : `/travel-packages/${slug}`;

      if (adminPreview) {
        response = await api.get(`/admin/partners/${adminPreviewPartnerId}/packages/preview/${slug}`);
        usedPartnerPreview = true;
      } else if (canUsePartnerPreview) {
        try {
          response = await api.get(`/partner-packages/packages/preview/${slug}`);
          usedPartnerPreview = true;
        } catch (partnerErr) {
          // Fall back to public API (e.g. published package viewed without ownership)
          if (partnerErr?.response?.status !== 404) {
            throw partnerErr;
          }
          response = await api.get(publicPackagePath);
        }
      } else {
        response = await api.get(publicPackagePath);
      }
      
      if (response.data.success) {
        const loaded = response.data.package;
        // Normalize pricing field used by the UI
        if ((!loaded.pricing || !loaded.pricing.length) && loaded.pricing_tiers?.length) {
          loaded.pricing = loaded.pricing_tiers;
        }
        setPkg(loaded);
        setIsPartnerDraftPreview(usedPartnerPreview && loaded.status && loaded.status !== 'published');
        if (loaded.pricing?.length > 0) {
          setSelectedPricing(loaded.pricing[0]);
        }
      } else {
        setError('notFound');
      }
    } catch (err) {
      console.error('Failed to load package:', err);
      setError('loadFailed');
    } finally {
      setLoading(false);
    }
  }, [slug, adminPreview, adminPreviewPartnerId, canUsePartnerPreview, clientToken, catalogToken]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  // Additional state for enhanced inquiry flow
  const [bookingRequested, setBookingRequested] = useState(false);
  const [referralCode, setReferralCode] = useState(null);

  const trackView = useCallback(async () => {
    if (!pkg?.id || partnerPreviewMode) return; // Don't count partner previews as public views
    try {
      const response = await api.post(`/package-booking/track-view/${pkg.id}`, {
        sessionId: sessionStorage.getItem('sessionId') || Math.random().toString(36).substr(2, 9),
        source: new URLSearchParams(window.location.search).get('ref') || 'direct'
      });
      if (response.data.referralCode) {
        setReferralCode(response.data.referralCode);
      }
    } catch (err) {
      console.error('Failed to track view:', err);
    }
  }, [pkg?.id, partnerPreviewMode]);

  // Track view and get referral code on load
  useEffect(() => {
    if (pkg?.id && !referralCode && !partnerPreviewMode) {
      trackView();
    }
  }, [pkg?.id, referralCode, trackView, partnerPreviewMode]);

  // Initialize Google Map with route
  useEffect(() => {
    if (!pkg?.items || pkg.items.length === 0 || !mapContainerRef.current) return;
    
    const initMap = async () => {
      try {
        await loadGoogleMaps();
        
        const geocoder = new window.google.maps.Geocoder();
        
        // Helper function to geocode a location
        const geocodeLocation = async (address) => {
          return new Promise((resolve) => {
            geocoder.geocode({ address }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve({
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng()
                });
              } else {
                resolve(null);
              }
            });
          });
        };
        
        // Collect unique locations from items, ordered by day
        const uniqueLocations = [];
        const seenLocations = new Set();
        
        // Sort items by day
        const sortedItems = [...pkg.items].sort((a, b) => (a.day_number || 1) - (b.day_number || 1));
        
        sortedItems.forEach(item => {
          const locationName = item.location || item.name;
          if (locationName && !seenLocations.has(locationName.toLowerCase())) {
            seenLocations.add(locationName.toLowerCase());
            uniqueLocations.push({
              day: item.day_number || 1,
              name: locationName,
              lat: item.latitude ? parseFloat(item.latitude) : null,
              lng: item.longitude ? parseFloat(item.longitude) : null
            });
          }
        });
        
        // Geocode locations that don't have coordinates (limit to first 8)
        const locationsToProcess = uniqueLocations.slice(0, 8);
        const locations = [];
        
        for (const loc of locationsToProcess) {
          if (loc.lat && loc.lng) {
            locations.push(loc);
          } else {
            // Try to geocode
            const coords = await geocodeLocation(loc.name);
            if (coords) {
              locations.push({
                ...loc,
                lat: coords.lat,
                lng: coords.lng
              });
            }
          }
        }
        
        // If still no locations, try the main destination
        if (locations.length === 0 && pkg.destination) {
          const coords = await geocodeLocation(pkg.destination);
          if (coords) {
            locations.push({
              day: 1,
              name: pkg.destination,
              lat: coords.lat,
              lng: coords.lng
            });
          }
        }
        
        if (locations.length === 0) {
          setMapLoaded(false);
          return;
        }
        
        // Create map centered on first location
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach(loc => bounds.extend({ lat: loc.lat, lng: loc.lng }));
        
        const map = new window.google.maps.Map(mapContainerRef.current, {
          center: bounds.getCenter(),
          zoom: 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e9f6' }] },
            { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
            { featureType: 'road', stylers: [{ visibility: 'simplified' }] },
            { featureType: 'poi', stylers: [{ visibility: 'off' }] }
          ]
        });
        
        mapRef.current = map;
        
        // Wait for map to be ready then fit bounds
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          if (locations.length > 1) {
            map.fitBounds(bounds, { top: 60, right: 240, bottom: 80, left: 20 });
          } else {
            map.setCenter(bounds.getCenter());
            map.setZoom(10);
          }
        });
        
        // Also fit bounds immediately with padding for sidebar
        map.fitBounds(bounds, { top: 60, right: 240, bottom: 80, left: 20 });
        
        // Draw route line FIRST (so it's behind markers)
        if (locations.length > 1) {
          const routePath = locations.map(loc => ({ lat: loc.lat, lng: loc.lng }));
          
          // Solid route line with brand color
          new window.google.maps.Polyline({
            path: routePath,
            geodesic: true,
            strokeColor: '#4A0E15',
            strokeOpacity: 1,
            strokeWeight: 4,
            map: map
          });
          
          // Dashed overlay for visual interest
          new window.google.maps.Polyline({
            path: routePath,
            geodesic: true,
            strokeColor: '#ffffff',
            strokeOpacity: 0,
            strokeWeight: 4,
            icons: [{
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 0.6,
                strokeColor: '#ffffff',
                scale: 2
              },
              offset: '0',
              repeat: '20px'
            }],
            map: map
          });
        }
        
        // Add markers ON TOP of route
        locations.forEach((loc, idx) => {
          const isStart = idx === 0;
          const isEnd = idx === locations.length - 1;
          
          // Create custom marker
          const marker = new window.google.maps.Marker({
            position: { lat: loc.lat, lng: loc.lng },
            map: map,
            title: loc.name,
            zIndex: isStart ? 1000 : isEnd ? 999 : 100 + idx,
            label: isStart ? '' : isEnd ? '' : { text: String(idx + 1), color: 'white', fontWeight: 'bold', fontSize: '11px' },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: isStart || isEnd ? 14 : 10,
              fillColor: isStart ? '#9C7B33' : '#4A0E15',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 3
            }
          });
          
          // Info window
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; min-width: 120px;">
                <strong style="font-size: 14px;">${loc.name}</strong>
                <div style="color: #666; font-size: 12px; margin-top: 4px;">
                  ${isStart ? '🚀 START' : isEnd ? '🏁 END' : `Day ${loc.day}`}
                </div>
              </div>
            `
          });
          
          marker.addListener('click', () => infoWindow.open(map, marker));
        });
        
        setMapLoaded(true);
      } catch (error) {
        console.warn('Error loading map:', error);
        setMapLoaded(false);
      }
    };
    
    initMap();
  }, [pkg]);

  const trackInquiryStarted = useCallback(() => {
    if (!pkg?.id || partnerPreviewMode) return;
    api.post(`/package-booking/track-view/${pkg.id}`, {
      sessionId: sessionStorage.getItem('sessionId') || Math.random().toString(36).substr(2, 9),
      source: new URLSearchParams(window.location.search).get('ref') || 'direct',
      action: 'inquiry_started',
    }).catch(() => {});
  }, [pkg?.id, partnerPreviewMode]);

  const handleAskAboutTrip = useCallback(() => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/packages/${slug}?ask=1`)}`);
      return;
    }
    setInquiryOpen(true);
  }, [user, navigate, slug]);

  const handleAskAdvisor = useCallback(() => {
    setAdvisorAskOpen(true);
  }, []);

  useEffect(() => {
    if (!pkg) return;
    if (searchParams.get('askAdvisor') === '1') {
      setAdvisorAskOpen(true);
    }
    if (user && searchParams.get('ask') === '1') {
      setInquiryOpen(true);
    }
  }, [user, pkg, searchParams]);

  const buyerInCalifornia = (buyerCountry || 'US').toUpperCase() === 'US'
    && ['CA', 'CALIFORNIA'].includes((buyerState || '').trim().toUpperCase());

  const handleBook = () => {
    if (pkg?.is_expired || isPackageExpired(pkg)) return;
    if (!user) {
      navigate('/login?redirect=' + encodeURIComponent(`/packages/${slug}`));
      return;
    }
    // Disclosures first — payment can only start after the buyer confirms.
    setDisclosureOpen(true);
  };

  const proceedWithBooking = async () => {
    setDisclosureOpen(false);
    if (pkg?.is_expired || isPackageExpired(pkg)) return;
    const travelersCount = Math.max(1, parseInt(inquiryTravelers, 10) || 1);

    // Direct Booking — embedded Stripe Checkout modal (same pattern as trip upgrades)
    if (pkg.booking_method === 'checkout') {
      if (!user) {
        navigate('/login?redirect=' + encodeURIComponent(`/packages/${slug}`));
        return;
      }

      try {
        setBookingLoading(true);
        setError(null);
        const addonItems = addOnSelectionsPayload(selectedAddOnQuantities);
        const response = await api.post('/package-booking/create-embedded-checkout', {
          packageId: pkg.id,
          pricingId: selectedPricing?.id,
          travelers: travelersCount,
          referralCode,
          clientToken,
          catalogToken,
          buyerCountry,
          buyerState,
          addonItems,
          addonItemIds: addonItems.map((item) => item.id),
        });

        if (response.data?.clientSecret) {
          setCheckoutData(response.data);
          setStripeCheckoutOpen(true);
        } else {
          setError('Failed to start checkout. Please try again.');
        }
      } catch (err) {
        console.error('Failed to start package checkout:', err);
        setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      } finally {
        setBookingLoading(false);
      }
      return;
    }

    if (!user) {
      navigate('/login?redirect=' + encodeURIComponent(`/packages/${slug}`));
      return;
    }

    try {
      setBookingLoading(true);
      const addonItems = addOnSelectionsPayload(selectedAddOnQuantities);
      const response = await api.post('/package-booking/request-booking', {
        packageId: pkg.id,
        pricingId: selectedPricing?.id,
        travelers: travelersCount,
        referralCode,
        clientToken,
        catalogToken,
        buyerCountry,
        buyerState,
        addonItems,
        addonItemIds: addonItems.map((item) => item.id),
      });

      if (response.data.success) {
        setBookingRequested(true);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to request booking:', err);
      setError('Failed to request booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleStripeCheckoutClose = () => {
    setStripeCheckoutOpen(false);
    setCheckoutData(null);
  };

  const handleStripeCheckoutSuccess = (result) => {
    handleStripeCheckoutClose();
    if (result?.tripId) {
      navigate(`/trips/${result.tripId}`);
      return;
    }
    navigate('/my-bookings');
  };

  const handleOpenBookForClient = useCallback(() => {
    setClientLinkError('');
    setClientLinkNotice('');
    setBookForClientOpen(true);
  }, []);

  const handleCopyClientLink = useCallback(async () => {
    if (!pkg?.id) return;
    setClientLinkError('');
    setClientLinkNotice('');
    setCopyLinkBusy(true);
    try {
      const response = await api.post(
        `/partner-packages/packages/${pkg.id}/client-checkout`,
        {},
        { usePartnerAuth: true }
      );
      const url = response.data?.url;
      if (!url) throw new Error('Missing client checkout url');
      try {
        await navigator.clipboard.writeText(url);
        setClientLinkNotice(t('publicPackage.clientLinkCopied'));
      } catch (clipboardError) {
        setClientLinkNotice(t('publicPackage.clientLinkCopyManual', { url }));
      }
    } catch (err) {
      console.error('Failed to create client checkout link:', err);
      setClientLinkError(t('publicPackage.clientLinkFailed'));
    } finally {
      setCopyLinkBusy(false);
    }
  }, [pkg?.id, t]);

  if (loading) {
    return (
      <Box sx={{ minHeight: embedded ? '100%' : '100vh', bgcolor: DOCUMENT.canvas, color: DOCUMENT.ink, py: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        <Typography sx={{ mt: 2, fontFamily: DOCUMENT.display }}>{t('publicPackage.loading')}</Typography>
      </Box>
    );
  }

  if (error || !pkg) {
    return (
      <Box sx={{ minHeight: embedded ? '100%' : '100vh', bgcolor: DOCUMENT.canvas, color: DOCUMENT.ink, px: 3, py: 6 }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720, mx: 'auto' }}>
          {error && ['notFound', 'loadFailed'].includes(error) ? t(`publicPackage.${error}`) : (error || t('publicPackage.notFound'))}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(getPackagesListingPath())} sx={partnerOutlineBtnSx}>
            {t('publicPackage.back')}
          </Button>
        </Box>
      </Box>
    );
  }

  const packageSchema = getPackageSchema(pkg);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://dejavunomad.com' },
    { name: 'Travel Packages', url: 'https://dejavunomad.com/deals?tab=packages' },
    { name: pkg?.title || 'Package', url: `https://dejavunomad.com/packages/${slug}` }
  ]);
  const metaDescription = generatePackageMetaDescription(pkg);
  const metaKeywords = generatePackageKeywords(pkg);

  const canShareWithClient = canUsePartnerPreview && isPackageShareable(pkg);

  return (
    <>
      {!partnerPreviewMode && !embedded && pkg && (
        <SEOHead 
          title={`${pkg.title} - ${pkg.destination} Travel Package`}
          description={metaDescription || pkg.description?.substring(0, 160)}
          keywords={metaKeywords}
          canonical={`https://dejavunomad.com/packages/${slug}`}
          ogImage={pkg.cover_image || '/noBgColor.png'}
          ogType="product"
          structuredData={[packageSchema, breadcrumbSchema].filter(Boolean)}
          siteName={pkg.branding?.level && pkg.branding.level !== 'house' ? pkg.branding.name : undefined}
        />
      )}

      {isPrivateLuxuryPackage(pkg) ? (
        <PrivateJourneyPackageView
          pkg={pkg}
          t={t}
          onRequestQuote={() => {
            trackInquiryStarted();
            handleAskAboutTrip();
          }}
          onCustomize={() => {
            trackInquiryStarted();
            handleAskAboutTrip();
          }}
        />
      ) : (
        <PublicPackagePage
          pkg={pkg}
          t={t}
          partnerPreviewMode={partnerPreviewMode}
          isPartnerDraftPreview={isPartnerDraftPreview}
          onClientCheckout={canShareWithClient ? handleOpenBookForClient : undefined}
          onCopyClientLink={canShareWithClient ? handleCopyClientLink : undefined}
          copyLinkBusy={copyLinkBusy}
          onEdit={canUsePartnerPreview && pkg?.id
            ? () => navigate(`/partner/packages/${pkg.id}/edit`)
            : undefined}
          clientLinkBusy={false}
          clientLinkError={clientLinkError}
          clientLinkNotice={clientLinkNotice}
          selectedPricing={selectedPricing}
          setSelectedPricing={setSelectedPricing}
          inquiryTravelers={inquiryTravelers}
          setInquiryTravelers={setInquiryTravelers}
          bookingLoading={bookingLoading}
          onBook={handleBook}
          onAsk={handleAskAboutTrip}
          onAskAdvisor={handleAskAdvisor}
          onExternalBook={() => window.open(pkg.external_booking_url, '_blank')}
          mapContainerRef={mapContainerRef}
          mapLoaded={mapLoaded}
          mapHasItems={Boolean(pkg.items?.length)}
          selectedAddOnQuantities={selectedAddOnQuantities}
          setSelectedAddOnQuantities={setSelectedAddOnQuantities}
        />
      )}

      <BookForClientDialog
        open={bookForClientOpen}
        onClose={() => setBookForClientOpen(false)}
        packageId={pkg?.id}
        packageTitle={pkg?.title}
        currency={pkg?.currency || 'USD'}
        pricePerPerson={selectedPricing?.price_per_person || pkg?.base_price || 0}
        pricingId={selectedPricing?.id || null}
        defaultTravelers={inquiryTravelers}
        onSent={(result) => {
          setClientLinkNotice(t('bookForClient.sent', {
            email: result.email,
            ref: result.bookingReference,
          }));
        }}
      />

      {/* Seller of Travel disclosures — must be confirmed before payment */}
      <Dialog open={disclosureOpen} onClose={() => setDisclosureOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('sellerDisclosures.checkoutTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 2 }}>{t('sellerDisclosures.checkoutIntro')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('sellerDisclosures.buyerCountry')}
              value={buyerCountry}
              onChange={(e) => setBuyerCountry(e.target.value.toUpperCase().slice(0, 2))}
              size="small"
              sx={{ width: 140 }}
              inputProps={{ 'data-testid': 'seller-buyer-country' }}
            />
            <TextField
              label={t('sellerDisclosures.buyerState')}
              value={buyerState}
              onChange={(e) => setBuyerState(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 160 }}
              inputProps={{ 'data-testid': 'seller-buyer-state' }}
            />
          </Box>
          <SellerDisclosureBox
            seller={sellerFromPackage(pkg)}
            buyerInCalifornia={buyerInCalifornia}
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={(
              <Checkbox
                checked={disclosureConfirmed}
                onChange={(e) => setDisclosureConfirmed(e.target.checked)}
                data-testid="seller-disclosure-confirm"
              />
            )}
            label={t('sellerDisclosures.confirmDisclosure')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisclosureOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!disclosureConfirmed || !buyerCountry || bookingLoading}
            onClick={proceedWithBooking}
            data-testid="seller-disclosure-continue"
            sx={partnerOxbloodBtnSx}
          >
            {t('sellerDisclosures.continueToPayment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Embedded Stripe Checkout — same modal used for trip upgrades */}
      {stripeCheckoutOpen && checkoutData?.clientSecret && (
        <StripeCheckout
          key={checkoutData.sessionId}
          open={stripeCheckoutOpen}
          onClose={handleStripeCheckoutClose}
          clientSecret={checkoutData.clientSecret}
          tripTitle={pkg?.title || 'Package'}
          upgradeType={selectedPricing?.tier_name || 'Package booking'}
          quantity={Math.max(1, parseInt(inquiryTravelers, 10) || 1)}
          onSuccess={handleStripeCheckoutSuccess}
        />
      )}

      <AdvisorInquiryForm
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        mode="package"
        partnerId={pkg?.partner_id}
        partnerName={pkg?.partner_name}
        packageId={pkg?.id}
        packageTitle={pkg?.title}
        selectedPricing={selectedPricing}
        travelers={inquiryTravelers}
        onTravelersChange={setInquiryTravelers}
        catalogToken={catalogToken}
        clientToken={clientToken}
        referralCode={referralCode}
        maxTravelers={pkg?.max_travelers || 20}
        onTrackStarted={trackInquiryStarted}
      />
      <AdvisorInquiryForm
        open={advisorAskOpen}
        onClose={() => setAdvisorAskOpen(false)}
        mode="general"
        partnerId={pkg?.partner_id}
        partnerName={pkg?.partner_name}
        catalogToken={catalogToken}
        clientToken={clientToken}
        onTrackStarted={trackInquiryStarted}
      />

      <DocumentPageOverlay
        open={bookingRequested}
        onClose={() => setBookingRequested(false)}
        title={t('publicPackage.bookingRequested')}
      >
        <Box sx={{ px: { xs: 2, md: 5 }, py: 4, maxWidth: 720 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('publicPackage.bookingRequestedBody')}
          </Alert>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>
            {t('publicPackage.bookingNext')}
          </Typography>
          <Box component="ol" sx={{ pl: 2 }}>
            <Typography component="li" sx={{ mb: 1 }}>
              {t('publicPackage.bookingNextReview')}
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              {t('publicPackage.bookingNextPay')}
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              {t('publicPackage.bookingNextTrip')}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ color: DOCUMENT.ink3 }}>
            {t('publicPackage.bookingTrack')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/my-bookings')} variant="outlined" sx={partnerOutlineBtnSx}>
              {t('nav.myBookings')}
            </Button>
            <Button onClick={() => setBookingRequested(false)} variant="contained" sx={partnerOxbloodBtnSx}>
              {t('publicPackage.continueBrowsing')}
            </Button>
          </Box>
        </Box>
      </DocumentPageOverlay>
    </>
  );
};

export default PublicPackageDetails;

